import gql from 'graphql-tag';
import _ from 'underscore';

import * as EventConstants from 'constants/EventConstants';
import * as FileBrowserDAO from 'file-browser/FileBrowserDAO';
import FolderDAOCache from 'file-browser/FolderDAOCache';
import {SchemaTag} from 'folder-items/FileConstants';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import AppDispatcher from 'lib/Dispatcher';
import navigateToURL from 'lib/navigateToURL';
import notifications from 'notifications';
import RegistryActions from 'registry/RegistryActions';
import * as RegistryUtil from 'registry/RegistryUtil';
import {
  GetSelectorForFieldTableQueryVariables,
  GetSelectorForFieldTableQuery,
} from 'registry/schema-field-selector/__generated/GetSelectorForFieldTableQuery';
import SchemaFieldSelectorDAO, {
  SchemaFieldSelectorForUpdate,
  SchemaFieldSelectorWithOptionToUpdate,
} from 'registry/schema-field-selector/SchemaFieldSelectorDAO';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {SchemaFieldSelector, SchemaFieldSelectorOptionForUpdate} from 'schemas/SchemaFieldSelectorConstants';

export const GET_SELECTOR_FOR_FIELD_TABLE_QUERY = gql`
  query GetSelectorForFieldTableQuery($id: StrictString!) @skipAudit @engComponent(name: SCHEMAS) {
    schemaFieldSelector(id: $id) {
      ... on SchemaFieldSelectorNode {
        ...SchemaFieldSelectorGraphQL
        registry {
          id
          organization {
            id
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('SchemaFieldSelector').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

const SchemaFieldSelectorActions = {
  async addCreatedSelectorToApolloCache(selector: SchemaFieldSelector): Promise<void> {
    // eslint-disable-next-line global-require
    const mod = require('registry/field/FieldTable');

    const selectorForCacheUpdate = await doQuerySafe<
      GetSelectorForFieldTableQuery,
      GetSelectorForFieldTableQueryVariables
    >({query: GET_SELECTOR_FOR_FIELD_TABLE_QUERY}, {variables: {id: selector.id}});
    const selectorGQL = selectorForCacheUpdate.schemaFieldSelector;
    if (!isInaccessible(selectorGQL)) {
      mod.addSelectorToFieldTableQueryCache(selectorGQL);
    }
  },

  cancelUpdate(): void {
    AppDispatcher.dispatch({
      actionType: EventConstants.SCHEMA_FIELD_SELECTOR_UPDATE_CANCELED,
    });
  },

  create(data: {
    name: string;
    registryId: string;
    schemaFieldSelectorOptions: Array<SchemaFieldSelectorOptionForUpdate>;
  }): JQueryPromise<SchemaFieldSelector> {
    const registryId = data.registryId;
    return SchemaFieldSelectorDAO.create(data)
      .done((response: SchemaFieldSelector): void => {
        AppDispatcher.dispatch({
          actionType: EventConstants.SCHEMA_FIELD_SELECTOR_CREATE_COMPLETED,
          registryId,
          selector: response,
        });
        SchemaFieldSelectorActions.addCreatedSelectorToApolloCache(response);
        notifications.success('Dropdown created', {
          actionText: 'Open',
          duration: 10,
          onActionClick: async () => {
            // Currently, we have to construct the dropdown URL by using the `registryId` and fetching the
            // `ownerUrlId`. Ideally, the server response would have the URL (`navigateToURL(response.url)`),
            // or the dropdown URL would simply only require the `selectorId`
            // (`navigateToURL(RegistryUtil.getSchemaFieldSelectorSettingsUrl(response.id))`).
            const ownerUrlId = await RegistryActions.getOwnerUrlId(registryId, {
              loadingMessage: 'Opening dropdown...',
            });
            const url = RegistryUtil.getSchemaFieldSelectorSettingsUrl(ownerUrlId, registryId, response.id);
            navigateToURL(url);
          },
        });
      })
      .fail((res: Response): void => {
        notifications.notifyError(res);
      });
  },

  delete(selectorId: string): void {
    SchemaFieldSelectorDAO.delete(selectorId)
      .done((response: {}) => {
        AppDispatcher.dispatch({
          actionType: EventConstants.SCHEMA_FIELD_SELECTOR_DELETE_COMPLETED,
          selectorId,
        });
        notifications.success('Dropdown deleted');
      })
      .fail((res: Response): void => {
        notifications.notifyError(res);
      });
  },

  /**
   * Fetch selectors for the given registry, or null to fetch all registries.
   */
  async fetch(registryId: string | null = null): Promise<void> {
    const response = await SchemaFieldSelectorDAO.fetch(registryId);
    AppDispatcher.dispatch({
      actionType: EventConstants.SCHEMA_FIELD_SELECTORS_FETCH_COMPLETED,
      selectorsByRegistryId: response!.selectorsByRegistryId,
    });
  },

  update(
    registryId: string,
    selectorId: string,
    data: SchemaFieldSelectorForUpdate
  ): JQueryPromise<SchemaFieldSelectorWithOptionToUpdate> {
    /**
     * response contains schemaFieldSelectorOptionToUpdate when an update tries to rename/delete an option
     * used as a tag value. schemaFieldSelectorOptionToUpdate contains info to inform user of their
     * decision.
     *
     * When successful, update all open files to display the new option value without needing a page refresh.
     */
    return SchemaFieldSelectorDAO.update(selectorId, data)
      .done((response: SchemaFieldSelectorWithOptionToUpdate) => {
        if (response.schemaFieldSelectorOptionToUpdate) {
          AppDispatcher.dispatch({
            actionType: EventConstants.SCHEMA_FIELD_SELECTOR_UPDATE_FAILED,
            selectorOptionToUpdate: response.schemaFieldSelectorOptionToUpdate,
          });
        } else {
          AppDispatcher.dispatch({
            actionType: EventConstants.SCHEMA_FIELD_SELECTOR_UPDATE_COMPLETED,
            registryId,
            selector: response,
          });
          notifications.success('Dropdown updated successfully');

          // Refresh open files and folders
          // 1. Refresh files
          // TODO: FileStates import inlined to handle circular import when importing SelectorInput from tests
          // Remove when filestates is refactored to not import the entire world
          // eslint-disable-next-line global-require
          const FileStates = require('FileStates').default;
          // eslint-disable-next-line no-restricted-syntax
          const fileIdsWithSelector = _.pluck(
            FileStates.get().filter(
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (fileState: any) =>
                fileState.model &&
                fileState.model() &&
                fileState.model().tagSchema() &&
                fileState
                  .model()
                  .tagSchema()
                  .fields.some((f: SchemaField) => f.schemaFieldSelectorId === response.id)
            ),
            'id'
          );

          FileBrowserDAO.bulkGetTags(fileIdsWithSelector).done(
            (bulkGetTagsResponse: {data: Array<{id: string; tags: Array<SchemaTag>}>}) => {
              // eslint-disable-next-line no-restricted-syntax
              _.map(bulkGetTagsResponse.data, ({id, tags}) => {
                AppDispatcher.dispatch({
                  actionType: EventConstants.FILE_TAGS_UPDATED,
                  // eslint-disable-next-line no-restricted-syntax
                  tagData: _.map(tags, (tag) => ({id, tags})),
                });
              });
            }
          );
          // 2. Refresh folders
          FolderDAOCache.invalidateAllFolders();
        }
      })
      .fail((res: Response): void => {
        notifications.notifyError(res);
      });
  },
};

export default SchemaFieldSelectorActions;
